import { combineLatest, map, Observable, of, switchMap } from 'rxjs'
import { OrderField } from '../models/order-search.model'
import { OrdersService } from '../orders.service'
import { Order, OrderDetail, OrderDetailData } from '../models/order.entity'
import { parseOrderDetailKeys } from '../libs/order.lib'
import { Carrier, CarriersService } from '../../carriers'
import { Warehouse, WarehousesService } from '../../warehouses'
import { Injectable } from '@angular/core'

@Injectable({
  providedIn: 'root',
})
export class OrdersDetailLoader {
  constructor(
    private carriersService: CarriersService,
    private ordersService: OrdersService,
    private warehousesService: WarehousesService,
  ) {}

  /**
   * Load order and external data if needed
   * @param orderId - the order ID
   * @param fields - the order fields
   */
  loadOrder$(orderId: string, fields: OrderField[]): Observable<OrderDetail> {
    return this.ordersService
      .read$(orderId)
      .pipe(
        switchMap((order) =>
          this._loadDetailExtData$(order, fields).pipe(
            map((extData) => ({ order, extData })),
          ),
        ),
      )
  }

  /**
   * Save order and load external data if needed
   * @param order - the order ID
   * @param fields - the order fields
   * @returns the observable for save order
   */
  saveOrder$(order: Order, fields: OrderField[]): Observable<OrderDetail> {
    return this.ordersService
      .upsert$(order)
      .pipe(
        switchMap((order) =>
          this._loadDetailExtData$(order, fields).pipe(
            map((extData) => ({ order, extData })),
          ),
        ),
      )
  }

  /**
   * Load external order data
   * @param order - the order
   * @param fields - the orders fields
   * @returns the observable for load external data
   */
  private _loadDetailExtData$(
    order: Order,
    fields: OrderField[],
  ): Observable<OrderDetailData> {
    const extDataKeys = parseOrderDetailKeys(order)
    const obs$: Partial<{
      carrier: Observable<Carrier>
      warehouse: Observable<Warehouse>
    }> = {}
    if (fields.includes('carrierId') && extDataKeys.carrierId) {
      obs$.carrier = this.carriersService.read$(extDataKeys.carrierId)
    }

    if (fields.includes('assignedWarehouseId') && extDataKeys.warehouseId) {
      obs$.warehouse = this.warehousesService.read$(extDataKeys.warehouseId)
    }

    if (!Object.keys(obs$).length) {
      return of({})
    }

    return combineLatest(obs$)
  }
}
